import { z } from 'zod';

import { ColorSchema, ShadeSchema } from '#core/schema/ColorSchema.ts';

export const LineStyleType = z
  .enum(['solid', 'dashed', 'dotted'])
  .describe('Line style for series: solid (default), dashed, or dotted');

/**
 * Chart type descriptions for LLM guidance.
 * Static object provides type-safe access and simple lookup.
 */
export const CHART_TYPE_DESCRIPTIONS = {
  area: 'Filled line chart for showing volume/magnitude over time',
  bar: 'Categorical comparison with vertical/horizontal bars',
  line: 'Trend visualization over time or continuous data',
  composite: 'Combined chart with multiple series types',
  donut: 'Part-to-whole with center space (≤6 parts)',
  pie: 'Part-to-whole relationships (≤6 parts)',
  funnel: 'Sequential process stages with drop-off',
  radar: 'Multi-dimensional comparison across categories',
  scatter: 'Correlation between two numeric variables',
  bubble: 'Scatter with third dimension as bubble size',
  radialBar: 'Progress or comparison in circular form',
  sparkline: 'Compact inline trend indicator',
  heatmap: 'Two-dimensional data intensity visualization',
} as const;

/** Chart type array derived from descriptions. */
export const CHART_TYPES = Object.keys(CHART_TYPE_DESCRIPTIONS) as (keyof typeof CHART_TYPE_DESCRIPTIONS)[];

export type ChartType = keyof typeof CHART_TYPE_DESCRIPTIONS;

/**
 * Gets chart type descriptions as a formatted string for LLM prompts.
 * @example "- area: Filled line chart for showing volume/magnitude over time"
 */
export function getChartTypeDescriptionsForPrompt(): string {
  return CHART_TYPES.map((t) => `- ${t}: ${CHART_TYPE_DESCRIPTIONS[t]}`).join('\n');
}

/**
 * Chart types that support bar/line series mixing in composite mode.
 */
export const COMPOSITE_SERIES_TYPES = ['bar', 'line', 'area'] as const;

/**
 * Configuration for a single data series in multi-series charts.
 */
export const ChartSeriesSchema = z.object({
  name: z.string(),
  label: z.string().optional().describe('Display label for this series in legend and tooltips'),
  color: ColorSchema.optional().describe('Color from Mantine theme palette. Auto-assigned if not specified.'),
  shade: ShadeSchema.optional().describe(
    'Color shade intensity (0-9). Lower = lighter, higher = darker. Default is 6.',
  ),
  type: z
    .enum(COMPOSITE_SERIES_TYPES)
    .catch('line')
    .describe('Override series type (bar or line) for composite charts')
    .prefault('line'),
  lineStyle: LineStyleType.catch('solid').prefault('solid'),
});

/** @public Input type for ChartSeriesSchema - use for component props (optional type/lineStyle) */
export type ChartSeriesInput = z.input<typeof ChartSeriesSchema>;

/** Output type for ChartSeriesSchema - use after Zod parsing (type/lineStyle guaranteed) */
export type ChartSeries = z.output<typeof ChartSeriesSchema>;

/**
 * Unified chart component schema for LLM and dashboard use.
 *
 * Strict schema - all props must be explicitly defined.
 *
 * Note: Uses z.unknown() for data values for runtime flexibility.
 * The transformToTaggedObjects() function replaces empty additionalProperties
 * with primitives during JSON Schema generation for API compatibility.
 */
export const ReferenceLineSchema = z.object({
  y: z.number().optional().describe('Y-axis value for a horizontal reference line'),
  x: z.union([z.string(), z.number()]).optional().describe('X-axis value for a vertical reference line'),
  label: z.string().optional().describe('Text label displayed alongside the reference line'),
  color: z.string().optional().describe('Mantine color with optional shade (e.g., "red.6", "blue.7")'),
});

export const ChartSchema = z.object({
  type: z.literal('chart').describe('Chart component type'),
  chartType: z.enum(CHART_TYPES).describe(`Chart visualization type. Options:\n${getChartTypeDescriptionsForPrompt()}`),
  data: z.array(z.record(z.string(), z.unknown())).optional().default([]).describe('Array of data points.'),
  xAxis: z.string().prefault('timestamp').describe('Property name for x-axis values.'),
  yAxis: z.string().describe('Property name for y-axis values'),
  title: z.string().optional().describe('Optional chart title'),
  series: z.array(ChartSeriesSchema).optional().describe('Multi-series configuration (for cartesian charts)'),
  curveType: z
    .enum(['linear', 'monotone', 'step', 'stepBefore', 'stepAfter', 'natural'])
    .optional()
    .describe(
      'Interpolation curve type. Use "stepBefore" for binary or discrete state data (e.g., online/offline status).',
    ),
  referenceLines: z
    .array(ReferenceLineSchema)
    .optional()
    .describe(
      'Horizontal or vertical reference lines for thresholds, limits, averages, or notable values. Use y for horizontal lines (e.g., production target) and x for vertical lines (e.g., event dates).',
    ),
});

/** @public Input type for ChartSchema - use for component props */
export type ChartSchemaInput = z.input<typeof ChartSchema>;

/** @public Output type for ChartSchema - use after Zod parsing */
export type ChartSchemaOutput = z.output<typeof ChartSchema>;

export type ReferenceLine = z.output<typeof ReferenceLineSchema>;

/**
 * Chart config passed to renderers.
 *
 * Equivalent to `ChartSchemaOutput` but with `data` narrowed from optional to
 * required. The Chart component guards against empty `data` before invoking a
 * renderer, so at this point `data` is guaranteed to be a non-empty array.
 */
export type ChartRendererConfig = ChartSchemaOutput & { data: Record<string, unknown>[] };
